"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import Link from "next/link";
import { createClient } from "@/lib/supabase";
import { GoogleSignInButton } from "@/app/components/GoogleSignInButton";
import { completeEmployerSignupFromSession } from "@/app/actions/employer-signup";
import { trackEvent, trackGA4Event } from "@/lib/analytics";
import type { CampaignAttribution } from "@/lib/campaign-attribution";
import { getSignupCampaignAttribution } from "@/lib/campaign-attribution-client";
import {
  saveEmployerSignupDraft,
  clearEmployerSignupDraft,
} from "@/lib/employer-signup-draft";
import {
  buildFirstListingTitle,
} from "@/lib/marketplace/first-listing";
import {
  HEARD_ABOUT_OPTIONS,
  HEARD_ABOUT_OTHER_MAX_LENGTH,
} from "@/lib/employer-signup/heard-about";
import { EMPLOYER_LOGIN_PATH } from "@/lib/account/personal-account-hiring";

const EMPLOYER_REGIONS = [
  { value: "French Alps", label: "French Alps" },
  { value: "Swiss Alps", label: "Swiss Alps" },
  { value: "Austrian Alps", label: "Austrian Alps" },
  { value: "Italian Alps", label: "Italian Alps" },
  { value: "Scandinavia", label: "Scandinavia" },
  { value: "Mediterranean", label: "Mediterranean" },
  { value: "South of France", label: "South of France" },
  { value: "Caribbean", label: "Caribbean" },
  { value: "UK", label: "UK" },
  { value: "North America", label: "North America" },
  { value: "Asia Pacific", label: "Asia Pacific" },
  { value: "Other", label: "Other" },
];

const ROLE_CATEGORY_OPTIONS = [
  { value: "ski", label: "Ski & Mountain" },
  { value: "yacht", label: "Yacht" },
  { value: "watersports", label: "Watersports" },
  { value: "festivals", label: "Festivals & Events" },
  { value: "agriculture", label: "Agriculture" },
  { value: "hospitality", label: "Hospitality" },
] as const;

const SEASON_TYPE_OPTIONS = [
  { value: "winter", label: "Winter", icon: "\u2744\uFE0F" },
  { value: "summer", label: "Summer", icon: "\u2600\uFE0F" },
  { value: "festivals", label: "Festivals", icon: "\uD83C\uDFAA" },
] as const;

const COMPANY_SIZES = [
  { value: "1-5", label: "1–5 employees" },
  { value: "6-20", label: "6–20 employees" },
  { value: "21-50", label: "21–50 employees" },
  { value: "51-100", label: "51–100 employees" },
  { value: "100+", label: "100+ employees" },
];

const INPUT_CLASS =
  "px-4 py-3 border-[1.5px] border-border rounded-[10px] text-sm font-sans text-charcoal bg-warm-bg transition-all outline-none focus:border-gold focus:shadow-[0_0_0_3px_rgba(196,151,90,0.1)] focus:bg-white placeholder:text-[#B0A99F]";

interface FormData {
  companyName: string;
  contactName: string;
  email: string;
  password: string;
  location: string;
  seasonTypes: string[];
  hiringRoleCategories: string[];
  companySize: string;
  heardAbout: string;
  heardAboutOther: string;
}

const INITIAL_FORM: FormData = {
  companyName: "",
  contactName: "",
  email: "",
  password: "",
  location: "",
  seasonTypes: [],
  hiringRoleCategories: [],
  companySize: "",
  heardAbout: "",
  heardAboutOther: "",
};

export function EmployerSignupForm({
  candidateCount,
  invite,
  completeProfile = false,
  initialEmail = "",
  initialName = "",
  campaignAttribution,
  entrySource = null,
}: {
  candidateCount: number | null;
  invite: { id: string; companyName: string; companyId: string } | null;
  completeProfile?: boolean;
  initialEmail?: string;
  initialName?: string;
  campaignAttribution?: CampaignAttribution | null;
  /** First-party page or CTA that sent the visitor (sanitised slug). */
  entrySource?: string | null;
}) {
  const [form, setForm] = useState<FormData>({
    ...INITIAL_FORM,
    companyName: invite?.companyName ?? "",
    email: initialEmail,
    contactName: initialName,
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showPassword, setShowPassword] = useState(false);
  const [awaitingConfirmation, setAwaitingConfirmation] = useState(false);
  const [autoSubmitting, setAutoSubmitting] = useState(false);
  const [postFirstRole, setPostFirstRole] = useState(false);
  const signupStartedRef = useRef(false);
  const completingRef = useRef(false);
  const [recoveryNeeded, setRecoveryNeeded] = useState(false);
  const [resent, setResent] = useState(false);

  function markSignupStarted() {
    if (signupStartedRef.current) return;
    signupStartedRef.current = trackEvent("employer_signup_started", {
      signup_mode: invite ? "invite" : "new_company",
      ...(getSignupCampaignAttribution(campaignAttribution) ?? {}),
    });
  }

  function updateField<K extends keyof FormData>(key: K, value: FormData[K]) {
    markSignupStarted();
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }

  function validate(): boolean {
    const errs: Record<string, string> = {};
    if (!invite && !form.companyName.trim())
      errs.companyName = "Company name is required";
    if (!form.email.trim()) {
      errs.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      errs.email = "Please enter a valid email";
    }
    if (!completeProfile && (!form.password || form.password.length < 8)) {
      errs.password = "Password must be at least 8 characters";
    }
    if (!invite && form.seasonTypes.length === 0) {
      errs.seasonTypes = "Select at least one season";
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  const finishAccountSetup = useCallback(async () => {
    if (completingRef.current) return;
    completingRef.current = true;
    setAutoSubmitting(true);
    setErrors({});
    try {
      // The server verifies the session and reuses the same atomic creation
      // path as the confirmation callback. No browser membership writes.
      const { created } = await completeEmployerSignupFromSession("metadata");
      if (!created) throw new Error("We could not finish your company setup. Please try again.");
      clearEmployerSignupDraft();
      trackGA4Event("sign_up", { method: "employer" });
      trackGA4Event("generate_lead", { method: "employer" });
      window.location.href = "/employer/welcome";
    } catch (err) {
      setErrors({ form: err instanceof Error ? err.message : "We could not finish your company setup. Please try again." });
      setRecoveryNeeded(true);
      setAwaitingConfirmation(true);
      setAutoSubmitting(false);
      setLoading(false);
    } finally {
      completingRef.current = false;
    }
  }, []);

  // Keep the Auth callback synchronous: Supabase holds its session lock here.
  // Complete on the next task so database/auth requests cannot deadlock it.
  useEffect(() => {
    if (!awaitingConfirmation) return;
    const supabase = createClient();
    let timer: ReturnType<typeof setTimeout> | undefined;
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if ((event === "SIGNED_IN" || event === "INITIAL_SESSION") && session?.user) {
        if (timer) clearTimeout(timer);
        timer = setTimeout(() => { void finishAccountSetup(); }, 0);
      }
    });
    return () => {
      subscription.unsubscribe();
      if (timer) clearTimeout(timer);
    };
  }, [awaitingConfirmation, finishAccountSetup]);

  // Safety timeout ref — clears on unmount
  const submitTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (submitTimeoutRef.current) clearTimeout(submitTimeoutRef.current);
    };
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate() || loading) return;

    setLoading(true);
    setErrors({});

    // Safety timeout — if the form is still loading after 10s, unblock it
    let timedOut = false;
    submitTimeoutRef.current = setTimeout(() => {
      timedOut = true;
      setLoading(false);
      setErrors({ form: "This is taking longer than expected. Please try again." });
    }, 10_000);

    try {
      const supabase = createClient();
      // 1. Save draft to localStorage
      const effectiveCompanyName = invite ? invite.companyName : form.companyName;
      const firstListing = postFirstRole && form.hiringRoleCategories.length > 0
        ? {
            roleCategory: form.hiringRoleCategories[0],
            seasonTypes: form.seasonTypes,
            region: form.location || null,
          }
        : undefined;
      const attribution = getSignupCampaignAttribution(campaignAttribution);
      const heardAbout = invite ? "" : form.heardAbout;
      const heardAboutFields = heardAbout
        ? {
            heardAbout,
            ...(heardAbout === "other" && form.heardAboutOther.trim()
              ? { heardAboutOther: form.heardAboutOther.trim().slice(0, HEARD_ABOUT_OTHER_MAX_LENGTH) }
              : {}),
          }
        : {};
      const sourceFields = entrySource ? { entrySource } : {};
      saveEmployerSignupDraft({
        version: 1,
        createdAt: new Date().toISOString(),
        companyName: effectiveCompanyName,
        contactName: form.contactName.trim(),
        email: form.email,
        location: form.location,
        industry: "",
        companySize: form.companySize,
        campaignAttribution: attribution,
        seasonTypes: form.seasonTypes,
        hiringRoleCategories: form.hiringRoleCategories,
        ...heardAboutFields,
        ...sourceFields,
        ...(invite ? { inviteId: invite.id, companyId: invite.companyId } : {}),
        ...(firstListing ? { firstListing } : {}),
      });

      // 2. Save to pending_employer_signups (server-side backup)
      const signupData = {
        companyName: effectiveCompanyName,
        contactName: form.contactName.trim(),
        email: form.email,
        location: form.location,
        companySize: form.companySize,
        campaignAttribution: attribution,
        seasonTypes: form.seasonTypes,
        hiringRoleCategories: form.hiringRoleCategories,
        ...heardAboutFields,
        ...sourceFields,
        ...(invite ? { inviteId: invite.id, companyId: invite.companyId } : {}),
        ...(firstListing ? { firstListing } : {}),
      };

      trackEvent("employer_signup_details_submitted", {
        signup_mode: invite ? "invite" : "new_company",
        signup_method: completeProfile ? "verified_session" : "email",
        first_listing_requested: Boolean(firstListing),
        ...(heardAbout ? { heard_about: heardAbout } : {}),
        ...(entrySource ? { entry_source: entrySource } : {}),
        ...(attribution ?? {}),
      });

      if (completeProfile) {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user?.email) throw new Error("Your session has expired. Log in again");
        const { error: metadataError } = await supabase.auth.updateUser({
          data: {
            signup_role: "employer",
            employer_signup: { ...signupData, email: user.email },
          },
        });
        if (metadataError) throw metadataError;
        const { created } = await completeEmployerSignupFromSession("metadata");
        if (!created) throw new Error("We could not finish your company setup");
        clearEmployerSignupDraft();
        trackGA4Event("sign_up", { method: "employer" });
        trackGA4Event("generate_lead", { method: "employer" });
        window.location.href = "/employer/welcome";
        return;
      }
      // Wrapped in its own try/catch — must never block the signup flow
      try {
        const { error: pendingError } = await supabase
          .from("pending_employer_signups")
          .upsert(
            {
              email: form.email.trim().toLowerCase(),
              signup_data: signupData,
            },
            // anon has an INSERT policy but no UPDATE policy on this table
            // (migration 20260521b), so the ON CONFLICT DO UPDATE path is
            // rejected by RLS on a retry. DO NOTHING keeps the retry from
            // erroring — the auth user metadata below is the durable copy.
            { onConflict: "email", ignoreDuplicates: true }
          );
        if (pendingError) {
          console.error("[employer-signup] pending_employer_signups upsert failed:", pendingError.message);
        }
      } catch {
        // Network error or table issue — non-blocking
      }

      if (timedOut) return;

      // 3. Create auth account (triggers confirmation email). Employer
      // intent + payload also ride along in the auth user metadata — the
      // durable primary channel through the email round-trip; the pending
      // row above is just a backup.
      const { data: signUpData, error: authError } = await supabase.auth.signUp({
        email: form.email,
        password: form.password,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`,
          data: { signup_role: "employer", employer_signup: signupData },
        },
      });

      // signUp resolved — clear the safety timer and honour the real result
      // even if the 10s timeout already fired. The auth account and the
      // confirmation email may now exist, so abandoning here would show a
      // false "taking longer than expected" and push the user into a
      // duplicate retry (which then errors as "account already exists").
      if (submitTimeoutRef.current) {
        clearTimeout(submitTimeoutRef.current);
        submitTimeoutRef.current = null;
      }

      if (authError) {
        setErrors({ form: authError.message });
        setLoading(false);
        return;
      }

      // Detect if the email is already registered (Supabase returns a user
      // with an empty identities array when the email already exists)
      if (signUpData?.user?.identities?.length === 0) {
        setErrors({ form: "DUPLICATE_EMAIL" });
        setLoading(false);
        return;
      }

      if (signUpData.session) {
        await finishAccountSetup();
        return;
      }

      trackEvent("employer_signup_confirmation_pending", {
        signup_mode: invite ? "invite" : "new_company",
        ...(attribution ?? {}),
      });

      // 4. Show confirmation screen (clear any premature timeout message)
      setErrors({});
      setLoading(false);
      setAwaitingConfirmation(true);
    } catch (err) {
      if (!timedOut) {
        const message = err instanceof Error ? err.message : "Something went wrong";
        setErrors({ form: `${message}. Please try again.` });
      }
    } finally {
      if (submitTimeoutRef.current) {
        clearTimeout(submitTimeoutRef.current);
        submitTimeoutRef.current = null;
      }
      if (!timedOut) {
        setLoading(false);
      }
    }
  }

  async function handleResendEmail() {
    setLoading(true);
    setErrors({});
    setResent(false);
    try {
      const supabase = createClient();
      const { error } = await supabase.auth.resend({
        type: "signup",
        email: form.email,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        },
      });
      if (error) {
        setErrors({ form: error.message });
      } else {
        setResent(true);
        trackEvent("employer_signup_confirmation_resent");
      }
    } catch {
      setErrors({ form: "Could not resend email. Please try again." });
    } finally {
      setLoading(false);
    }
  }

  // ═══ AWAITING CONFIRMATION SCREEN ═══
  if (autoSubmitting) {
    return (
      <div className="max-w-[480px] mx-auto px-5 pb-20 text-center">
        <div className="bg-white rounded-[22px] p-10 border border-border-light shadow-[0_4px_32px_rgba(0,0,0,0.04)]">
          <div className="w-10 h-10 border-[3px] border-gold border-t-transparent rounded-full animate-spin mx-auto mb-5" />
          <h2 className="font-serif text-2xl mb-2">Setting up your account</h2>
          <p className="text-[13px] text-text-muted">
            Creating your company profile...
          </p>
        </div>
      </div>
    );
  }

  if (awaitingConfirmation) {
    return (
      <div className="max-w-[480px] mx-auto px-5 pb-20 text-center">
        <div className="bg-white rounded-[22px] p-10 border border-border-light shadow-[0_4px_32px_rgba(0,0,0,0.04)]">
          <div className="w-14 h-14 rounded-full bg-gold-pale flex items-center justify-center mx-auto mb-5">
            <svg
              width="28"
              height="28"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="text-gold"
            >
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
              <polyline points="22,6 12,13 2,6" />
            </svg>
          </div>
          <h2 className="font-serif text-[28px] mb-3">Check your email</h2>
          <p className="text-[14px] text-text-muted leading-relaxed mb-6">
            We&apos;ve sent a confirmation link to{" "}
            <strong className="text-charcoal">{form.email}</strong>. Click the
            link to activate your account.
          </p>
          <p className="text-[12px] text-text-muted mb-6">
            Check your spam folder if you don&apos;t see it within a few minutes.
          </p>

          {errors.form && (
            <div className="bg-error/10 border border-error/30 text-error rounded-[10px] px-4 py-3 mb-4 text-sm font-medium">
              {errors.form}
            </div>
          )}

          {resent && <p role="status" className="mb-4 text-sm text-text-muted">Confirmation email sent. Check your inbox.</p>}
          {recoveryNeeded && (
            <button type="button" onClick={() => { void finishAccountSetup(); }} className="min-h-[44px] w-full rounded-lg bg-navy px-4 py-3 text-white mb-3">
              Try finishing account setup
            </button>
          )}
          <button
            onClick={handleResendEmail}
            disabled={loading}
            className="min-h-[44px] text-[13px] text-gold font-semibold bg-transparent border-none cursor-pointer font-sans hover:text-gold-light transition-colors disabled:opacity-50"
          >
            {loading ? "Sending..." : "Resend confirmation email"}
          </button>
        </div>
      </div>
    );
  }

  // ═══ SIGNUP FORM ═══
  return (
    <div className="max-w-[520px] mx-auto px-5 pb-16 md:pb-20">
      {/* Header */}
      <div className="text-center mb-8">
        {invite ? (
          <>
            <h1 className="font-serif text-[36px] md:text-[42px] mb-2">
              Join <em className="text-gold italic">{invite.companyName}</em>
            </h1>
            <p className="text-[14px] text-text-muted leading-relaxed">
              You&apos;ve been invited to join {invite.companyName} on PeakWave.
              Create your account to get started.
            </p>
          </>
        ) : (
          <>
            <h1 className="font-serif text-[36px] md:text-[42px] mb-2">
              Start <em className="text-gold italic">hiring</em>
            </h1>
            <p className="text-[14px] text-text-muted leading-relaxed">
              Find exceptional seasonal staff for your business. Create your
              company account in under 2 minutes.
            </p>
          </>
        )}
      </div>

      <form
        onSubmit={handleSubmit}
        onChangeCapture={markSignupStarted}
        noValidate
        className="bg-white rounded-[22px] p-7 md:p-10 border border-border-light shadow-[0_4px_32px_rgba(0,0,0,0.04)]"
      >
        {errors.form && (
          <div className="bg-error/10 border border-error/30 text-error rounded-[10px] px-4 py-3 mb-6 text-sm font-medium">
            {errors.form === "DUPLICATE_EMAIL" ? (
              <>
                <span className="block">
                  This email already has a PeakWave account.
                </span>
                <span className="mt-1 block font-normal">
                  If it is your employer account,{" "}
                  <Link href={EMPLOYER_LOGIN_PATH} className="underline font-semibold">
                    log in to hire
                  </Link>
                  . If it is a personal account (for Companion, conditions or
                  finding work), employer accounts need a different email,
                  such as jobs@yourcompany.com. Enter that above to continue.
                </span>
              </>
            ) : (
              errors.form
            )}
          </div>
        )}

        {/* Section: Account Details */}
        {!completeProfile ? (
          <GoogleSignInButton role="employer" invite={invite?.id} source={entrySource ?? undefined} campaignAttribution={campaignAttribution} disabled={loading} />
        ) : (
          <p className="mb-6 text-sm text-text-muted">
            You are signed in. Add your company details to finish setting up your free employer account.
          </p>
        )}

        <div className="text-[11px] font-semibold uppercase tracking-[1.5px] text-text-muted mb-5">
          Account Details
        </div>

        {/* Company name — hidden when joining via invite */}
        {!invite && (
          <div className="flex flex-col mb-5">
            <label htmlFor="employer-companyName" className="text-xs font-semibold uppercase tracking-[0.8px] text-charcoal mb-2">
              Company name <span className="text-terracotta">*</span>
            </label>
            <input
              type="text"
              placeholder="Alpine Chalets Co."
              id="employer-companyName"
              value={form.companyName}
              onChange={(e) => updateField("companyName", e.target.value)}
              className={INPUT_CLASS}
            />
            {errors.companyName && (
              <p className="text-xs text-error mt-1">{errors.companyName}</p>
            )}
          </div>
        )}

        {/* Your name (optional) */}
        <div className="flex flex-col mb-5">
          <label htmlFor="employer-contactName" className="text-xs font-semibold uppercase tracking-[0.8px] text-charcoal mb-2">
            Your name{" "}
            <span className="font-normal normal-case tracking-normal text-text-muted">
              (optional)
            </span>
          </label>
          <input
            type="text"
            placeholder="e.g. Sam Rivers"
            id="employer-contactName"
            value={form.contactName}
            onChange={(e) => updateField("contactName", e.target.value)}
            className={INPUT_CLASS}
          />
        </div>

        {/* Email */}
        <div className="flex flex-col mb-5">
          <label htmlFor="employer-email" className="text-xs font-semibold uppercase tracking-[0.8px] text-charcoal mb-2">
            Work email <span className="text-terracotta">*</span>
          </label>
          <input
            type="email"
            placeholder="hire@alpinechalets.com"
            disabled={completeProfile}
            id="employer-email"
            value={form.email}
            onChange={(e) => updateField("email", e.target.value)}
            className={INPUT_CLASS}
          />
          <p className="text-[11px] text-text-muted mt-1">
            {completeProfile ? "Your verified account email" : "We'll send a confirmation link to this address"}
          </p>
          {errors.email && (
            <p className="text-xs text-error mt-1">{errors.email}</p>
          )}
        </div>

        {/* Password */}
        {!completeProfile && (
        <div className="flex flex-col mb-5">
          <label htmlFor="employer-password" className="text-xs font-semibold uppercase tracking-[0.8px] text-charcoal mb-2">
            Password <span className="text-terracotta">*</span>
          </label>
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              placeholder="Minimum 8 characters"
              id="employer-password"
              value={form.password}
              onChange={(e) => updateField("password", e.target.value)}
              className={`${INPUT_CLASS} w-full pr-11`}
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-charcoal transition-colors min-h-[44px] min-w-[44px] p-1 bg-transparent border-none cursor-pointer"
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/><path d="M14.12 14.12a3 3 0 1 1-4.24-4.24"/></svg>
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
              )}
            </button>
          </div>
          {errors.password && (
            <p className="text-xs text-error mt-1">{errors.password}</p>
          )}
        </div>
        )}

        {/* Season types — required, hidden for invite flow */}
        {!invite && (
          <div className="flex flex-col mb-5">
            <label className="text-xs font-semibold uppercase tracking-[0.8px] text-charcoal mb-2">
              Which seasons do you hire for? <span className="text-terracotta">*</span>
            </label>
            <div className="flex flex-wrap gap-2">
              {SEASON_TYPE_OPTIONS.map((opt) => {
                const selected = form.seasonTypes.includes(opt.value);
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => {
                      const next = selected
                        ? form.seasonTypes.filter((s) => s !== opt.value)
                        : [...form.seasonTypes, opt.value];
                      updateField("seasonTypes", next);
                    }}
                    className={`px-3.5 py-2.5 min-h-[44px] rounded-full text-[13px] font-semibold border-[1.5px] transition-all cursor-pointer font-sans ${
                      selected
                        ? "bg-navy text-white border-navy"
                        : "bg-transparent text-text-body border-border hover:border-navy/30"
                    }`}
                  >
                    {opt.icon} {opt.label}
                  </button>
                );
              })}
            </div>
            {errors.seasonTypes && (
              <p className="text-xs text-error mt-1">{errors.seasonTypes}</p>
            )}
          </div>
        )}

        {/* Section: About Your Company (optional) — hidden for invite flow */}
        {!invite && (
          <>
            <div className="flex items-center gap-3 my-7 text-text-muted text-xs">
              <span className="flex-1 h-px bg-border" />
              <span>
                About Your Company{" "}
                <span className="font-normal text-text-muted">
                  (optional, add later from dashboard)
                </span>
              </span>
              <span className="flex-1 h-px bg-border" />
            </div>

            {/* Location */}
            <div className="flex flex-col mb-5">
              <label htmlFor="employer-location" className="text-xs font-semibold uppercase tracking-[0.8px] text-charcoal mb-2">
                Company location{" "}
                <span className="font-normal normal-case tracking-normal text-text-muted">
                  (optional)
                </span>
              </label>
              <select
                id="employer-location"
              value={form.location}
                onChange={(e) => updateField("location", e.target.value)}
                className={INPUT_CLASS}
              >
                <option value="">Select region</option>
                {EMPLOYER_REGIONS.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Roles */}
            <div className="flex flex-col mb-5">
              <label className="text-xs font-semibold uppercase tracking-[0.8px] text-charcoal mb-2">
                What roles do you typically hire?{" "}
                <span className="font-normal normal-case tracking-normal text-text-muted">
                  (optional)
                </span>
              </label>
              <div className="flex flex-wrap gap-2">
                {ROLE_CATEGORY_OPTIONS.map((opt) => {
                  const selected = form.hiringRoleCategories.includes(opt.value);
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => {
                        const next = selected
                          ? form.hiringRoleCategories.filter((r) => r !== opt.value)
                          : [...form.hiringRoleCategories, opt.value];
                        updateField("hiringRoleCategories", next);
                      }}
                      className={`px-3.5 py-2.5 min-h-[44px] rounded-full text-[13px] font-semibold border-[1.5px] transition-all cursor-pointer font-sans ${
                        selected
                          ? "bg-navy text-white border-navy"
                          : "bg-transparent text-text-body border-border hover:border-navy/30"
                      }`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Post first role opt-in — only shown when role category + season are chosen */}
            {form.hiringRoleCategories.length > 0 && form.seasonTypes.length > 0 && (
              <label className="flex items-start gap-3 rounded-[12px] border border-border bg-warm-bg p-4 cursor-pointer mb-5">
                <input
                  type="checkbox"
                  checked={postFirstRole}
                  onChange={(e) => { markSignupStarted(); setPostFirstRole(e.target.checked); }}
                  className="mt-0.5"
                />
                <span className="text-[13px] text-charcoal">
                  <span className="font-semibold">Post my first role as soon as I verify.</span>
                  <span className="block text-text-muted mt-0.5">
                    We will publish a listing you can edit any time:
                    {" "}
                    &ldquo;{buildFirstListingTitle({
                      roleCategory: form.hiringRoleCategories[0],
                      seasonTypes: form.seasonTypes,
                      region: form.location || null,
                    })}&rdquo;
                  </span>
                </span>
              </label>
            )}

            {/* Company size */}
            <div className="flex flex-col mb-5">
              <label htmlFor="employer-companySize" className="text-xs font-semibold uppercase tracking-[0.8px] text-charcoal mb-2">
                Company size{" "}
                <span className="font-normal normal-case tracking-normal text-text-muted">
                  (optional)
                </span>
              </label>
              <select
                id="employer-companySize"
              value={form.companySize}
                onChange={(e) => updateField("companySize", e.target.value)}
                className={INPUT_CLASS}
              >
                <option value="">Select size</option>
                {COMPANY_SIZES.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>

            {/* How did you hear about us */}
            <div className="flex flex-col mb-5">
              <label htmlFor="employer-heardAbout" className="text-xs font-semibold uppercase tracking-[0.8px] text-charcoal mb-2">
                How did you hear about PeakWave?{" "}
                <span className="font-normal normal-case tracking-normal text-text-muted">
                  (optional)
                </span>
              </label>
              <select
                id="employer-heardAbout"
                value={form.heardAbout}
                onChange={(e) => updateField("heardAbout", e.target.value)}
                className={`${INPUT_CLASS} min-h-[44px]`}
              >
                <option value="">Select an answer</option>
                {HEARD_ABOUT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {form.heardAbout === "other" && (
                <>
                  <label htmlFor="employer-heardAboutOther" className="sr-only">
                    Where did you hear about PeakWave?
                  </label>
                  <input
                    type="text"
                    id="employer-heardAboutOther"
                    placeholder="Tell us where"
                    maxLength={HEARD_ABOUT_OTHER_MAX_LENGTH}
                    value={form.heardAboutOther}
                    onChange={(e) => updateField("heardAboutOther", e.target.value)}
                    className={`${INPUT_CLASS} mt-2 min-h-[44px]`}
                  />
                </>
              )}
            </div>
          </>
        )}

        {/* Submit */}
        <button
          type="submit"
          disabled={loading}
          className="w-full py-4 bg-navy text-white rounded-[10px] text-[15px] font-bold border-none cursor-pointer font-sans transition-all hover:bg-gold hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(196,151,90,0.3)] disabled:opacity-50 disabled:cursor-not-allowed mt-2"
        >
          {loading ? "Creating account..." : "Create Account →"}
        </button>

        <p className="text-center text-xs text-text-muted mt-4">
          By signing up you agree to our{" "}
          <Link href="/terms" className="text-gold">
            Terms
          </Link>{" "}
          and{" "}
          <Link href="/privacy" className="text-gold">
            Privacy Policy
          </Link>
          .
        </p>

        {/* Social proof */}
        {candidateCount !== null && candidateCount > 0 && (
          <p className="text-center text-[12px] text-text-muted mt-4">
            <strong className="text-charcoal">{candidateCount}</strong>{" "}
            candidates open to employers
          </p>
        )}

        <div className="text-center text-[13px] text-text-muted mt-5">
          <span>
            Already have an account?{" "}
            <Link href="/login" className="text-gold font-semibold">
              Log in
            </Link>
          </span>
          <span className="mx-2">&middot;</span>
          <span>
            <Link href="/join" className="text-gold font-semibold">
              Looking for work instead?
            </Link>
          </span>
        </div>
      </form>
    </div>
  );
}
